/**
 * Open-world floor (spec §3): paper, grid within the fog radius, survey
 * callouts, landmark pillars, horizon band. Drawn every frame (no bake — the
 * camera moves). Blueprint register: ink hairlines on paper, fog toward paper.
 */
import { VIRTUAL_H, VIRTUAL_W } from '../../../sim/constants.js'
import type { Arena, Team } from '../../../sim/types.js'
import { aheadOnFloor, clipPolygonNear, clipSegmentNear, project, projectCamSpace, toCameraSpace, toTeamLocal, type CamSpace, type CameraParams, type Projected } from '../camera.js'
import { INK_RGB } from './constants.js'
import { CALLOUT_RADIUS, CALLOUT_STEP, GRID_MINOR, MAX_GRID_LINES, MAX_PILLARS, callouts, cullPillars, fog, gridLines } from './openworld-math.js'
import { drawPillar, pillarHeight } from './pillars.js'

const PAPER = 'rgb(12,14,17)'
/** Distance haze: what the far floor fades into and what the sky is at the horizon, so the two meet without a seam. */
const HAZE = 'rgb(30,33,38)'
const HAZE_RGB = '30,33,38'
const FLOOR = 'rgb(38,42,47)'

export interface FloorOpts { fogNear: number; fogFar: number; strokeOnly: boolean }

export class OpenWorldFloor {
  /** Camera position in WORLD space (pose is team-local; undo the mirror). */
  static cameraWorld(cam: CameraParams, team: Team, arena: Arena): { x: number; z: number } {
    const w = toTeamLocal(cam.pose.x, cam.pose.z, team, arena.length) // involution
    return { x: w.lx, z: w.lz }
  }

  draw(ctx: CanvasRenderingContext2D, arena: Arena, team: Team, cam: CameraParams, o: FloorOpts): { lines: number; pillars: number } {
    const P = (x: number, y: number, z: number): Projected => project(x, y, z, team, arena, cam)
    const CS = (x: number, z: number): CamSpace => toCameraSpace(x, 0, z, team, arena, cam)
    const c = OpenWorldFloor.cameraWorld(cam, team, arena)
    ctx.fillStyle = PAPER
    ctx.fillRect(0, 0, VIRTUAL_W, VIRTUAL_H)
    const hw = arena.width / 2
    const far = o.fogFar
    // Horizon row of the ground plane (a floor point very far ahead along the camera forward).
    const hz = aheadOnFloor(cam, 1e4, team, arena)
    const hy = Math.max(0, Math.min(VIRTUAL_H, P(hz.x, 0, hz.z).sy))
    // Sky: paper at the top easing into the haze at the horizon, so the horizon is a soft meeting line, not a bar.
    const skyTop = Math.max(0, hy - 220)
    const sky = ctx.createLinearGradient(0, skyTop, 0, hy)
    sky.addColorStop(0, PAPER); sky.addColorStop(1, HAZE)
    ctx.fillStyle = sky; ctx.fillRect(0, skyTop, VIRTUAL_W, Math.max(0, hy - skyTop))
    // Deck plate: the WHOLE deck (its real edges are the only edges), clipped to the near plane.
    const plate = clipPolygonNear([CS(-hw, 0), CS(hw, 0), CS(hw, arena.length), CS(-hw, arena.length)], cam.near).map((p) => projectCamSpace(p, cam))
    if (plate.length >= 3) {
      ctx.fillStyle = o.strokeOnly ? PAPER : FLOOR
      ctx.beginPath(); plate.forEach((p, i) => (i ? ctx.lineTo(p.sx, p.sy) : ctx.moveTo(p.sx, p.sy))); ctx.closePath(); ctx.fill()
    }
    // Distance fog on the floor, applied in screen space AFTER the grid and callouts (below) so a line running
    // away from the camera fades along its length: clear at fogNear ahead, full haze from fogFar to the horizon.
    const drawFog = (): void => {
      const nz = aheadOnFloor(cam, o.fogNear, team, arena), fz = aheadOnFloor(cam, far, team, arena)
      const yNear = P(nz.x, 0, nz.z).sy, yFar = Math.max(hy, P(fz.x, 0, fz.z).sy)
      if (yNear <= yFar) return
      const fogG = ctx.createLinearGradient(0, yFar, 0, yNear)
      fogG.addColorStop(0, `rgba(${HAZE_RGB},1)`); fogG.addColorStop(1, `rgba(${HAZE_RGB},0)`)
      ctx.fillStyle = fogG; ctx.fillRect(0, yFar, VIRTUAL_W, yNear - yFar)
      if (yFar > hy) { ctx.fillStyle = HAZE; ctx.fillRect(0, hy, VIRTUAL_W, yFar - hy) }
    }
    // Fog square for the grid/callouts/pillars (each fades by its own depth).
    const x0 = Math.max(-hw, c.x - far), x1 = Math.min(hw, c.x + far), z0 = Math.max(0, c.z - far), z1 = Math.min(arena.length, c.z + far)
    // Grid: each line is a segment clipped to the fog square and the near plane, alpha by fog at its nearest point.
    let lines = 0
    ctx.lineWidth = 1
    for (const l of gridLines(c.x, c.z, far, GRID_MINOR, MAX_GRID_LINES)) {
      if (l.axis === 'x' ? l.at < -hw || l.at > hw : l.at < 0 || l.at > arena.length) continue
      const seg = clipSegmentNear(l.axis === 'x' ? CS(l.at, z0) : CS(x0, l.at), l.axis === 'x' ? CS(l.at, z1) : CS(x1, l.at), cam.near)
      if (!seg) continue
      const a = projectCamSpace(seg[0], cam), b = projectCamSpace(seg[1], cam)
      const nearest = l.axis === 'x' ? P(l.at, 0, Math.min(z1, Math.max(z0, c.z))) : P(Math.min(x1, Math.max(x0, c.x)), 0, l.at)
      const alpha = (l.major ? 0.45 : 0.18) * (1 - fog(Math.max(0, nearest.depth), o.fogNear, far))
      if (alpha < 0.01) continue
      ctx.strokeStyle = `rgba(${INK_RGB},${alpha})`
      ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy); ctx.stroke()
      lines++
    }
    // Survey callouts at 50 m intersections.
    const terminal = arena.props.find((p) => p.kind === 'terminal') ?? { x: 0, z: arena.length / 2 }
    ctx.font = '10px ui-monospace, monospace'; ctx.textBaseline = 'middle'; ctx.textAlign = 'left'
    for (const k of callouts(c.x, c.z, terminal, CALLOUT_STEP, CALLOUT_RADIUS)) {
      const p = P(k.x, 0, k.z); if (!p.visible) continue
      const alpha = 0.6 * (1 - fog(p.depth, o.fogNear, far)); if (alpha < 0.02) continue
      ctx.strokeStyle = `rgba(${INK_RGB},${alpha})`; ctx.fillStyle = `rgba(${INK_RGB},${alpha})`
      const s = Math.max(4, 0.6 * p.scale)
      ctx.beginPath(); ctx.moveTo(p.sx - s, p.sy); ctx.lineTo(p.sx + s, p.sy); ctx.moveTo(p.sx, p.sy - s * 0.35); ctx.lineTo(p.sx, p.sy + s * 0.35); ctx.stroke()
      ctx.fillText(k.text, p.sx + s + 3, p.sy)
    }
    drawFog()
    // Landmark pillars, back to front.
    const near = cullPillars(arena.pillars, c.x, c.z, far, MAX_PILLARS)
    const items = near.map((pl) => ({ pl, base: P(pl.x, 0, pl.z), top: P(pl.x, pillarHeight(pl), pl.z) })).filter((e) => e.base.visible).sort((a, b) => b.base.depth - a.base.depth)
    for (const e of items) drawPillar(ctx, e.pl, e.base, e.top, o.strokeOnly, 1 - fog(e.base.depth, o.fogNear, far))
    return { lines, pillars: items.length }
  }
}
